import {Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {selectFundReturnSummaryTable, selectIsUndrawnComplete, selectPreviewReturnSummaryError, selectReturnSummary} from "../../store/fund-valuation/fund-valuation.selectors";
import {CodeTableEnum} from "../../../shared/model/code";
import {FormBuilder, Validators} from "@angular/forms";
import {map, Subscription, take} from "rxjs";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {FundValuationActions, ReturnSummaryActions} from "../../store/fund.actions";
import {selectIsAnyFundFormInEditMode, selectRelatedDeal} from "../../store/fund/fund.selectors";
import {FundValuationScenario} from "../../models/fund-valuation-scenario";
import {EMPTY_FUND_VALUATION} from "../../models/fund-valuation";
import {ActivatedRoute, Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {GoalSeekDialogComponent, GoalSeekDialogData} from "../../../shared/components/goal-seek-dialog/goal-seek-dialog.component";

@Component({
    selector: "valumize-fund-return-summary",
    templateUrl: "./fund-return-summary.component.html",
    styleUrls: ["./fund-return-summary.component.scss"],
    standalone: false
})
export class FundReturnSummaryComponent implements OnInit, OnDestroy {

    @Input() isOnSensitivity = false;

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];
    returnSummary$ = this.store.select(selectFundReturnSummaryTable);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);
    isUndrawnComplete$ = this.store.select(selectIsUndrawnComplete);
    previewError$ = this.store.select(selectPreviewReturnSummaryError);

    isEditable = false;
    fundValuation = EMPTY_FUND_VALUATION;
    scenarios: FundValuationScenario[] = [];
    codeTableScenarioType = CodeTableEnum.SHARED_SCENARIOTYPE;

    fundValuationForm = this.formBuilder.group({
        bidPriceAdjustment: this.formBuilder.control<number | null>(null, Validators.required),
        discountToNAV: this.formBuilder.control<number | null>(null),
    });

    ngOnInit() {
        this.subscriptions.push(this.store.select(selectReturnSummary).pipe(map((returnSummary) => {
            this.isEditable = returnSummary.isEditable;
            this.scenarios = returnSummary.scenarios;

            if (!returnSummary.isLoading) {
                this.fundValuation = returnSummary.fundValuation;
                const bidPriceAdjustment = DecimalFormatPipe.transformFromMillionsNum(returnSummary.fundValuation.bidPriceAdjustment.amount ?? undefined) ?? null;
                const discountToNAV = DecimalFormatPipe.transformFractionToPercent(returnSummary.fundValuation.discountToNAV.fraction ?? undefined) ?? null;
                this.fundValuationForm.patchValue({bidPriceAdjustment, discountToNAV}, {emitEvent: false});
            }
        })).subscribe());
    }

    onBidPriceAdjustmentChange() {
        const bidPriceAdjustment = DecimalFormatPipe.transformToMillionsNum(this.fundValuationForm.controls.bidPriceAdjustment.value ?? undefined);
        this.store.dispatch(ReturnSummaryActions.calc({
            bidPriceAdjustment,
            editedField: "BID_PRICE_ADJUSTMENT"
        }));
    }

    onDiscountToNAVChange() {
        const discountToNAV = DecimalFormatPipe.transformPercentToFraction(this.fundValuationForm.controls.discountToNAV.value ?? undefined);
        this.store.dispatch(ReturnSummaryActions.calc({
            discountToNAV,
            editedField: "DISCOUNT_TO_NAV"
        }));
    }

    save() {
        const bidPriceAdjustmentAmount = DecimalFormatPipe.transformToMillionsNum(this.fundValuationForm.controls.bidPriceAdjustment.value ?? undefined);
        const discountToNAVFraction = DecimalFormatPipe.transformPercentToFraction(this.fundValuationForm.controls.discountToNAV.value ?? undefined);
        const fundValuationToSave = {
            ...this.fundValuation,
            bidPriceAdjustment: {
                ...this.fundValuation.bidPriceAdjustment,
                amount: bidPriceAdjustmentAmount
            },
            discountToNAV: {
                ...this.fundValuation.discountToNAV,
                fraction: discountToNAVFraction
            }
        };
        this.store.dispatch(FundValuationActions.save({fundValuation: fundValuationToSave, scenarios: this.scenarios}));
    }

    openGoalSeekDialog() {
        const fv = this.fundValuation;
        this.subscriptions.push(
            this.store.select(selectRelatedDeal).pipe(take(1)).subscribe((deal) => {
                const hasDeal = deal != null;
                const data: GoalSeekDialogData = {
                    mode: "fund",
                    hasDeal,
                    goalSeekTvpi: fv.goalSeekTvpi?.factor ?? undefined,
                    goalSeekTvpiActive: fv.goalSeekTvpiActive?.value ?? false,
                    goalSeekIrr: fv.goalSeekIrr?.fraction ?? undefined,
                    goalSeekIrrActive: fv.goalSeekIrrActive?.value ?? false,
                    goalSeekDiscount: fv.goalSeekDiscount?.fraction ?? undefined,
                    goalSeekDiscountActive: fv.goalSeekDiscountActive?.value ?? false,
                    dealGoalSeekTvpi: deal?.goalSeekTvpi?.factor ?? undefined,
                    dealGoalSeekIrr: deal?.goalSeekIrr?.fraction ?? undefined,
                    dealGoalSeekDiscount: deal?.goalSeekDiscount?.fraction ?? undefined,
                };

                this.dialog.open(GoalSeekDialogComponent, {
                    data,
                    width: hasDeal ? "680px" : "500px",
                    maxHeight: "90vh"
                });
            })
        );
    }

    editMode = () => this.store.dispatch(ReturnSummaryActions.edit());

    cancel = () => this.store.dispatch(ReturnSummaryActions.cancel());


    openSensitivityPage() {
        this.router.navigate(
            ["valuation", "sensitivity"],
            {
                relativeTo: this.route,
                queryParamsHandling: "preserve",
            }
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
